import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { 
  Server, 
  Copy, 
  Check, 
  Play,
  Loader2,
  Clock,
  ExternalLink,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const STATUS_CODES = [
  { value: "200", label: "200 OK" },
  { value: "201", label: "201 Created" },
  { value: "204", label: "204 No Content" },
  { value: "400", label: "400 Bad Request" },
  { value: "401", label: "401 Unauthorized" },
  { value: "403", label: "403 Forbidden" },
  { value: "404", label: "404 Not Found" },
  { value: "500", label: "500 Internal Server Error" },
  { value: "502", label: "502 Bad Gateway" },
  { value: "503", label: "503 Service Unavailable" },
];

const SAMPLE_RESPONSES = {
  success: JSON.stringify({
    success: true,
    data: {
      id: "usr_123",
      email: "user@example.com",
      name: "John Doe",
      created_at: new Date().toISOString()
    }
  }, null, 2),
  error: JSON.stringify({
    error: true,
    message: "Resource not found",
    code: "NOT_FOUND"
  }, null, 2),
  list: JSON.stringify({
    data: [
      { id: 1, name: "Item 1" },
      { id: 2, name: "Item 2" },
      { id: 3, name: "Item 3" }
    ],
    meta: {
      total: 3,
      page: 1,
      per_page: 10
    }
  }, null, 2),
};

export const ApiResponseSimulator = () => {
  const [endpointId, setEndpointId] = useState<string | null>(null);
  const [mockUrl, setMockUrl] = useState<string>("");
  const [displayUrl, setDisplayUrl] = useState<string>("");
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [testResult, setTestResult] = useState<{ status: number; body: string } | null>(null);
  
  const [config, setConfig] = useState({
    path: "/api/users",
    method: "GET",
    statusCode: 200,
    responseBody: SAMPLE_RESPONSES.list,
    contentType: "application/json",
    delay: 0,
  });

  const createEndpoint = async () => {
    setIsCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke("mock-api/create", {
        method: "POST",
        body: {
          path: config.path,
          method: config.method,
          statusCode: config.statusCode,
          responseBody: config.responseBody,
          contentType: config.contentType,
          delay: config.delay,
        },
      });

      if (error) throw error;

      setEndpointId(data.endpointId);
      setMockUrl(data.url);
      setDisplayUrl(`https://insights.itdukes.com/api/mock/${data.endpointId}`);
      toast.success("Mock endpoint created! Valid for 24 hours.");
    } catch (err) {
      console.error("Create error:", err);
      toast.error("Failed to create mock endpoint");
    } finally {
      setIsCreating(false);
    }
  };

  const updateEndpoint = async () => {
    if (!endpointId) return;
    
    setIsUpdating(true);
    try {
      const { error } = await supabase.functions.invoke(`mock-api/config/${endpointId}`, {
        method: "PUT",
        body: {
          path: config.path,
          method: config.method,
          statusCode: config.statusCode,
          responseBody: config.responseBody,
          contentType: config.contentType,
          delay: config.delay,
        },
      });

      if (error) throw error;
      toast.success("Endpoint updated!");
    } catch (err) {
      console.error("Update error:", err);
      toast.error("Failed to update endpoint");
    } finally {
      setIsUpdating(false);
    }
  };

  const testEndpoint = async () => {
    if (!mockUrl) return;
    
    setIsTesting(true);
    setTestResult(null);
    try {
      const response = await fetch(mockUrl, {
        method: config.method,
        headers: config.method !== "GET" ? { "Content-Type": "application/json" } : undefined,
        body: config.method !== "GET" ? JSON.stringify({ test: true }) : undefined,
      });

      const body = await response.text();
      setTestResult({
        status: response.status,
        body,
      });
      toast.success(`Response received: ${response.status}`);
    } catch (err) {
      console.error("Test error:", err);
      toast.error("Failed to test endpoint");
    } finally {
      setIsTesting(false);
    }
  };

  const copyUrl = async () => {
    if (!mockUrl) return;
    try {
      await navigator.clipboard.writeText(mockUrl);
      setCopied(true);
      toast.success("Endpoint URL copied to clipboard!", {
        description: "The functional API URL has been copied.",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy URL");
    }
  };

  const loadTemplate = (template: keyof typeof SAMPLE_RESPONSES) => {
    setConfig(prev => ({ ...prev, responseBody: SAMPLE_RESPONSES[template] }));
    toast.success("Template loaded");
  };

  const getMethodColor = (method: string) => {
    switch (method) {
      case "GET": return "bg-blue-500/20 text-blue-600 border-blue-500/30";
      case "POST": return "bg-green-500/20 text-green-600 border-green-500/30";
      case "PUT": return "bg-yellow-500/20 text-yellow-600 border-yellow-500/30";
      case "PATCH": return "bg-orange-500/20 text-orange-600 border-orange-500/30";
      case "DELETE": return "bg-red-500/20 text-red-600 border-red-500/30";
      default: return "bg-muted text-muted-foreground";
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <p className="text-muted-foreground">
          Create real mock API endpoints to test your frontend. Endpoints are valid for 24 hours.
        </p>
      </div>

      {/* Mock URL Display */}
      {mockUrl && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-3">
              <Server className="w-5 h-5 text-primary" />
              <span className="font-medium">Your Mock API Endpoint</span>
              <Badge variant="outline" className={getMethodColor(config.method)}>
                {config.method}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-background border rounded-lg px-4 py-2 text-sm font-mono break-all">
                {displayUrl}
              </code>
              <Button size="icon" variant="outline" onClick={copyUrl}>
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
              <Button size="icon" variant="outline" onClick={testEndpoint} disabled={isTesting}>
                {isTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Powered by IT Dukes API • The functional URL is copied when you click Copy.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Endpoint Configuration */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Configure Mock Response</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Method & Path */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label>Method</Label>
              <Select 
                value={config.method} 
                onValueChange={(v) => setConfig(prev => ({ ...prev, method: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GET">GET</SelectItem>
                  <SelectItem value="POST">POST</SelectItem>
                  <SelectItem value="PUT">PUT</SelectItem>
                  <SelectItem value="PATCH">PATCH</SelectItem>
                  <SelectItem value="DELETE">DELETE</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Path (for reference)</Label>
              <Input
                value={config.path}
                onChange={(e) => setConfig(prev => ({ ...prev, path: e.target.value }))}
                placeholder="/api/endpoint"
                className="font-mono"
              />
            </div>
          </div>

          {/* Status & Delay */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Status Code</Label>
              <Select 
                value={config.statusCode.toString()} 
                onValueChange={(v) => setConfig(prev => ({ ...prev, statusCode: parseInt(v) }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_CODES.map((code) => (
                    <SelectItem key={code.value} value={code.value}>
                      {code.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="w-3 h-3" />
                Delay: {config.delay}ms
              </Label>
              <Slider
                value={[config.delay]}
                onValueChange={([v]) => setConfig(prev => ({ ...prev, delay: v }))}
                min={0}
                max={5000}
                step={100}
              />
            </div>
          </div>

          {/* Response Body */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Response Body</Label>
              <div className="flex gap-1">
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => loadTemplate("success")}
                  className="text-xs h-7"
                >
                  Success
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => loadTemplate("error")}
                  className="text-xs h-7"
                >
                  Error
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => loadTemplate("list")}
                  className="text-xs h-7"
                >
                  List
                </Button>
              </div>
            </div>
            <Textarea
              value={config.responseBody}
              onChange={(e) => setConfig(prev => ({ ...prev, responseBody: e.target.value }))}
              placeholder='{"key": "value"}'
              className="font-mono min-h-[200px] text-sm"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            {!endpointId ? (
              <Button onClick={createEndpoint} disabled={isCreating} className="flex-1">
                {isCreating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Server className="w-4 h-4 mr-2" />
                    Create Mock Endpoint
                  </>
                )}
              </Button>
            ) : (
              <>
                <Button onClick={updateEndpoint} disabled={isUpdating} className="flex-1">
                  {isUpdating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Update Endpoint
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setEndpointId(null);
                    setMockUrl("");
                    setDisplayUrl("");
                    setTestResult(null);
                  }}
                >
                  New
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Test Result */}
      {testResult && (
        <Card className="bg-muted/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Play className="w-4 h-4" />
              Test Result
              <Badge variant={testResult.status < 400 ? "default" : "destructive"}>
                {testResult.status}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="bg-background border rounded-lg p-3 text-xs font-mono overflow-x-auto max-h-[200px]">
              {testResult.body}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Usage Instructions */}
      <Card className="bg-muted/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <ExternalLink className="w-5 h-5 text-primary mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-foreground mb-1">How to use your mock endpoint</p>
              <p className="text-muted-foreground mb-2">
                Use the generated URL in your application or test with curl:
              </p>
              {displayUrl && (
                <code className="block bg-background border rounded-lg px-3 py-2 text-xs font-mono">
                  curl -X {config.method} "{displayUrl}"
                </code>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};